import React from 'react';
import { Select, Input, Card, RadioGroup, RadioButton } from 'components';
import { CreateListingCardProps, ExteriorFields } from '../types';
import { StyledGrid } from './StyledComponents';

type ExteriorProps = CreateListingCardProps<ExteriorFields>;
type FieldsMapType = {
  freehold: {
    sale: string[];
    lease: string[];
  };
  land: {
    sale: string[];
    lease: string[];
  };
  income: {
    sale: string[];
    lease: string[];
  };
  commercial: string[];
};
const residentialFieldsMap = {
  sale: [
    'type',
    'style',
    'exterior',
    'lot_front',
    'lot_depth',
    'garage_type',
    'garage_spaces',
    'drive',
    'parking_spaces',
  ],
  lease: [
    'type',
    'style',
    'exterior',
    'lot_front',
    'lot_depth',
    'garage_type',
    'garage_spaces',
    'drive',
    'parking_spaces',
    'parking_cost',
    'parking_included',
    'private_entrance',
  ],
};
const fieldsMap: FieldsMapType = {
  freehold: residentialFieldsMap,
  land: residentialFieldsMap,
  income: residentialFieldsMap,
  commercial: [
    'type',
    'exterior',
    'garage_type',
    'lot_front',
    'lot_depth',
    'parking_spaces',
  ],
};

const Exterior: React.FC<ExteriorProps> = ({
  type,
  subtype,
  exteriorType,
  style,
  exterior,
  lot_front,
  lot_depth,
  garage_type,
  garage_spaces,
  drive,
  parking_spaces,
  parking_cost,
  parking_included,
  private_entrance,
  setListing,
}) => {
  const getFields = () => {
    if (['freehold', 'land', 'income'].includes(type!)) {
      return fieldsMap[type as 'freehold' | 'land' | 'income'][subtype!] || [];
    }
    if (type === 'commercial') return fieldsMap[type];
    return [];
  };

  const fields = getFields();

  const getExteriorTypeOptions = () => {
    const residentialExteriorFieldsMap = {
      sale: [
        'Apt/Row/Twnhouse',
        'Cottage',
        'Detached',
        'Det w/Com Elements',
        'Duplex',
        'Farm',
        'Fourplex',
        'Link',
        'Mobile/Trailer',
        'Multiplex',
        'Other',
        'Rural Resid',
        'Semi-Detached',
        'Store w/Apt/Offc',
        'Triplex',
        'Vacant Land',
      ],
      lease: [
        'Apt/Row/Twnhouse',
        'Cottage',
        'Detached',
        'Det w/Com Elements',
        'Duplex',
        'Farm',
        'Fourplex',
        'Link',
        'Lower Level',
        'Mobile/Trailer',
        'Multiplex',
        'Other',
        'Room',
        'Rural Resid',
        'Semi-Detached',
        'Shared Room',
        'Store w/Apt/Offc',
        'Triplex',
        'Upper Level',
        'Vacant Land',
      ],
    };
    const exteriorMap = {
      freehold: residentialExteriorFieldsMap,
      land: residentialExteriorFieldsMap,
      commercial: [
        'Commercial/Retail',
        'Farm',
        'Industrial',
        'Investment',
        'Land',
        'Office',
        'Sale Of Business',
        'Store w/Apt/Office',
      ],
    };
    let result = [];
    if (type === 'commercial') {
      result = exteriorMap.commercial;
    } else {
      result = exteriorMap.freehold[subtype!];
    }
    return result.map(value => ({ label: value, value }));
  };

  return (
    <Card type="primary" title="Exterior" id="exterior">
      <StyledGrid $columns={3}>
        {fields.includes('type') && (
          <Select
            allowClear
            label="Type"
            value={exteriorType}
            onChange={value =>
              setListing?.(prev => ({ ...prev, exteriorType: value }))
            }
            options={getExteriorTypeOptions()}
            data-cy="listing-type"
          />
        )}
        {fields.includes('style') && (
          <Select
            allowClear
            label="Style"
            value={style}
            onChange={value =>
              setListing?.(prev => ({ ...prev, style: value }))
            }
            options={[
              { label: '1-Storey', value: '1-Storey' },
              { label: '1 1/2 Storey', value: '1 1/2 Storey' },
              { label: '2-Storey', value: '2-Storey' },
              { label: '2 1/2 Storey', value: '2 1/2 Storey' },
              { label: '3-Storey', value: '3-Storey' },
              { label: 'Backsplit 3', value: 'Backsplit 3' },
              { label: 'Backsplit 4', value: 'Backsplit 4' },
              { label: 'Backsplit 5', value: 'Backsplit 5' },
              { label: 'Bungaloft', value: 'Bungaloft' },
              { label: 'Bungalow', value: 'Bungalow' },
              { label: 'Bungalow-Raised', value: 'Bungalow-Raised' },
              { label: 'Other', value: 'Other' },
              { label: 'Sideplit 3', value: 'Sideplit 3' },
              { label: 'Sideplit 4', value: 'Sideplit 4' },
              { label: 'Sideplit 5', value: 'Sideplit 5' },
            ]}
            data-cy="listing-style"
          />
        )}
        {fields.includes('exterior') && (
          <Select
            allowClear
            label="Exterior"
            value={exterior}
            onChange={value =>
              setListing?.(prev => ({ ...prev, exterior: value }))
            }
            options={[
              { label: 'Aluminium Siding', value: 'Aluminium Siding' },
              { label: 'Board & Batten', value: 'Board & Batten' },
              { label: 'Brick', value: 'Brick' },
              { label: 'Brick Front', value: 'Brick Front' },
              { label: 'Concrete', value: 'Concrete' },
              { label: 'Insulbrick', value: 'Insulbrick' },
              { label: 'Log', value: 'Log' },
              { label: 'Metal/Steel Siding', value: 'Metal/Steel Siding' },
              { label: 'Other', value: 'Other' },
              { label: 'Shingle', value: 'Shingle' },
              { label: 'Stone', value: 'Stone' },
              { label: 'Stucco (Plaster)', value: 'Stucco (Plaster)' },
              { label: 'Vinyl Siding', value: 'Vinyl Siding' },
              { label: 'Wood', value: 'Wood' },
            ]}
            data-cy="listing-exterior"
          />
        )}
        {fields.includes('garage_type') && (
          <Select
            allowClear
            label="Garage Type"
            value={garage_type}
            onChange={value =>
              setListing?.(prev => ({ ...prev, garage_type: value }))
            }
            options={
              type === 'commercial'
                ? [
                    { label: 'Boulevard', value: 'Boulevard' },
                    { label: 'Covered', value: 'Covered' },
                    { label: 'Double Detached', value: 'Double Detached' },
                    { label: 'In/Out', value: 'In/Out' },
                    { label: 'Lane', value: 'Lane' },
                    { label: 'None', value: 'None' },
                    { label: 'Other', value: 'Other' },
                    { label: 'Outside/Surface', value: 'Outside/Surface' },
                    { label: 'Pay', value: 'Pay' },
                    { label: 'Plaza', value: 'Plaza' },
                    { label: 'Public', value: 'Public' },
                    {
                      label: 'Reserved/Assigned',
                      value: 'Reserved/Assignd',
                    },
                    { label: 'Single Detached', value: 'Single Detached' },
                    { label: 'Street', value: 'Street' },
                    { label: 'Underground', value: 'Underground' },
                    { label: 'Valet', value: 'Valet' },
                    { label: 'Visitor', value: 'Visitor' },
                  ]
                : [
                    { label: 'Attached', value: 'Attached' },
                    { label: 'Built-in', value: 'Built-in' },
                    { label: 'Carport', value: 'Carport' },
                    { label: 'Detached', value: 'Detached' },
                    { label: 'None', value: 'None' },
                    { label: 'Other', value: 'Other' },
                    { label: 'Surface', value: 'Surface' },
                    { label: 'Undergrnd', value: 'Undergrnd' },
                  ]
            }
            data-cy={`listing-${
              type === 'commercial' ? 'commercial-' : ''
            }garage-type`}
          />
        )}
        {fields.includes('lot_front') && (
          <Input
            label="Lot Front (ft)"
            value={lot_front}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, lot_front: value }))
            }
            data-cy="listing-lot-front"
          />
        )}
        {fields.includes('lot_depth') && (
          <Input
            label="Lot Depth (ft)"
            value={lot_depth}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, lot_depth: value }))
            }
            data-cy="listing-lot-depth"
          />
        )}

        {fields.includes('garage_spaces') && (
          <Input
            label="Garage Spaces"
            value={garage_spaces}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, garage_spaces: value }))
            }
            data-cy="listing-garage-spaces"
          />
        )}
        {fields.includes('drive') && (
          <Select
            allowClear
            label="Drive"
            value={drive}
            onChange={value =>
              setListing?.(prev => ({ ...prev, drive: value }))
            }
            options={[
              { label: 'Available', value: 'Available' },
              { label: 'Circular', value: 'Circular' },
              { label: 'Front Yard', value: 'Front Yard' },
              { label: 'Lane', value: 'Lane' },
              { label: 'Mutual', value: 'Mutual' },
              { label: 'None', value: 'None' },
              { label: 'Other', value: 'Other' },
              { label: 'Private', value: 'Private' },
              { label: 'Pvt Double', value: 'Pvt Double' },
              { label: 'Rt-Of-Way', value: 'Rt-Of-Way' },
            ]}
            data-cy="listing-drive"
          />
        )}
        {fields.includes('parking_spaces') && (
          <Input
            label="Parking Spaces"
            value={parking_spaces}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, parking_spaces: value }))
            }
            data-cy="listing-parking-spaces"
          />
        )}
        {fields.includes('parking_cost') && (
          <Input
            label="Parking Cost (monthly)"
            value={parking_cost}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, parking_cost: value }))
            }
          />
        )}
        {fields.includes('parking_included') && (
          <RadioGroup
            allowClear
            label="Parking Incl"
            buttonStyle="solid"
            value={parking_included}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, parking_included: value }))
            }
          >
            <RadioButton value="No" data-cy="listing-parking-incl-no">
              No
            </RadioButton>
            <RadioButton value="Yes" data-cy="listing-parking-incl-yes">
              Yes
            </RadioButton>
          </RadioGroup>
        )}
        {fields.includes('private_entrance') && (
          <RadioGroup
            allowClear
            label="Private Entrance"
            buttonStyle="solid"
            value={private_entrance}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, private_entrance: value }))
            }
          >
            <RadioButton value="No" data-cy="listing-private-entrance-no">
              No
            </RadioButton>
            <RadioButton value="Yes" data-cy="listing-private-entrance-yes">
              Yes
            </RadioButton>
          </RadioGroup>
        )}
      </StyledGrid>
    </Card>
  );
};

export default React.memo(Exterior);
